import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useSWRInfinite from 'swr/infinite';
import { useSWRConfig } from 'swr';
import useDebounce from 'hooks/useDebounce';
import useFiltersState from 'state/useFiltersState';
import { COLUMN_TYPE } from '../TrelloColumn/TrelloColumnTypes';
import { BORD_ONE_TONE_ICONS } from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon.types';
import { ORDER_STATUS_ID, ORDER_STATUS_ID_CANCELLED } from 'types/orders';
import { SORT_ORDER, ImoduleFilter } from 'types/global';
import FinanceCard from 'components/CardInOrderList/FinanceCard/FinanceCard';
import OrdersTrackingFinanceFilters from '../OrdersFinanceTrackingFilters/OrdersTrackingFinanceFilters';
import useOrdersFinancesModule from './useOrdersFinancesModule';
import { getShipmentByStatusFinances, getShipmentOrderFinancesCancelled } from 'services/orders';
import OrdersCancelledModal from '../OrdersCancelledModal/OrdersCancelledModal';
import ButtonCancelOrders from '../ButtonCancelOrders/ButtonCancelOrders';
import { optionsParamsSwr } from 'utils/optionsParamsSwr';
import { useGetSWRInfiniteKey, hasMorePages } from 'utils/swrInfinite.utils';
import UnsuccessfulSearch from 'components/unsuccessfulSearch';
import {
  SEARCH_TYPE_TO_FILTER_CONFIG,
  formatOrderFinances,
  getUniqueOrderFinancesByOrderId
} from '../../trackingOrder.utils';
import TrelloColumn from '../TrelloColumn/TrelloColumn';
import { processQueryParams } from 'utils/ordersQueryParams.utils';
import './OrdersListingFinances.scss';

const DEBOUNCE_DURATION = 1500;

type OrdersListingFinancesContentProps = {
  queryParams: string;
  setQueryParams: (queryString: string) => void;
  openCancelOrdersModal: boolean;
  onCloseCancelOrdersModal: () => void;
  onLoadingChange: (isLoading: boolean) => void;
};

const OrdersListingFinancesContent = ({
  queryParams,
  setQueryParams,
  openCancelOrdersModal,
  onCloseCancelOrdersModal,
  onLoadingChange
}: OrdersListingFinancesContentProps) => {
  const swrKeyPending = useMemo(
    () => (queryParams ? ['orders-finances-pending', ORDER_STATUS_ID.PENDING_FOR_CONFIRMATION, queryParams] : null),
    [queryParams]
  );
  const swrKeyConfirmed = useMemo(
    () => (queryParams ? ['orders-finances-confirmed', ORDER_STATUS_ID.CONFIRMED, queryParams] : null),
    [queryParams]
  );
  const swrKeyDelivered = useMemo(
    () => (queryParams ? ['orders-finances-delivered', ORDER_STATUS_ID.DELIVERED, queryParams] : null),
    [queryParams]
  );

  const getPendingOrdersKey = useGetSWRInfiniteKey(swrKeyPending);
  const getConfirmedOrdersKey = useGetSWRInfiniteKey(swrKeyConfirmed);
  const getDeliveredOrdersKey = useGetSWRInfiniteKey(swrKeyDelivered);

  const {
    data: pendingOrdersPages = [],
    isLoading: isLoadingPending,
    isValidating: isValidatingPending,
    setSize: setPendingSize,
    mutate: mutatePendingOrders
  } = useSWRInfinite(
    getPendingOrdersKey,
    (key: (string | number)[]) => {
      const pageIndex = key?.[key?.length - 1] as number;
      const processedParams = processQueryParams(queryParams);
      return getShipmentByStatusFinances({
        statusId: [ORDER_STATUS_ID.PENDING_FOR_CONFIRMATION],
        page: pageIndex + 1,
        size: 10,
        additionalQueryParams: processedParams
      });
    },
    { ...optionsParamsSwr(), revalidateFirstPage: false, revalidateOnFocus: false }
  );

  const {
    data: confirmedOrdersPages = [],
    isLoading: isLoadingConfirmed,
    isValidating: isValidatingConfirmed,
    setSize: setConfirmedSize,
    mutate: mutateConfirmedOrders
  } = useSWRInfinite(
    getConfirmedOrdersKey,
    (key: (string | number)[]) => {
      const pageIndex = key?.[key?.length - 1] as number;
      const processedParams = processQueryParams(queryParams);
      return getShipmentByStatusFinances({
        statusId: [
          ORDER_STATUS_ID.CONFIRMED,
          ORDER_STATUS_ID.IN_PREPARATION,
          ORDER_STATUS_ID.TRANSIT_TO_WAREHOUSE,
          ORDER_STATUS_ID.IN_WAREHOUSE,
          ORDER_STATUS_ID.PREPARATION,
          ORDER_STATUS_ID.TRANSIT_TO_DESTINATION
        ],
        page: pageIndex + 1,
        size: 10,
        additionalQueryParams: processedParams
      });
    },
    { ...optionsParamsSwr(), revalidateFirstPage: false, revalidateOnFocus: false }
  );

  const {
    data: deliveredOrdersPages = [],
    isLoading: isLoadingDelivered,
    isValidating: isValidatingDelivered,
    setSize: setDeliveredSize,
    mutate: mutateDeliveredOrders
  } = useSWRInfinite(
    getDeliveredOrdersKey,
    (key: (string | number)[]) => {
      const pageIndex = key?.[key?.length - 1] as number;
      const processedParams = processQueryParams(queryParams);
      return getShipmentByStatusFinances({
        statusId: [ORDER_STATUS_ID.DELIVERED],
        page: pageIndex + 1,
        size: 10,
        additionalQueryParams: processedParams
      });
    },
    { ...optionsParamsSwr(), revalidateFirstPage: false, revalidateOnFocus: false }
  );

  const mutateFunctionsRef = useRef({
    mutatePendingOrders,
    mutateConfirmedOrders,
    mutateDeliveredOrders
  });

  const pendingOrders = useMemo(() => getUniqueOrderFinancesByOrderId(pendingOrdersPages.flat()), [pendingOrdersPages]);
  const confirmedOrders = useMemo(
    () => getUniqueOrderFinancesByOrderId(confirmedOrdersPages.flat()),
    [confirmedOrdersPages]
  );
  const deliveredOrders = useMemo(
    () => getUniqueOrderFinancesByOrderId(deliveredOrdersPages.flat()),
    [deliveredOrdersPages]
  );

  const isLoadingAnyOrderStatus = isLoadingPending || isLoadingConfirmed || isLoadingDelivered;

  const handleOrderStatusChange = useCallback(async () => {
    const {
      mutatePendingOrders: mutatePending,
      mutateConfirmedOrders: mutateConfirmed,
      mutateDeliveredOrders: mutateDelivered
    } = mutateFunctionsRef.current;
    if (mutatePending) await mutatePending(undefined, true);
    if (mutateConfirmed) await mutateConfirmed(undefined, true);
    if (mutateDelivered) await mutateDelivered(undefined, true);
  }, []);

  const updateMutateFunctionsRef = () => {
    mutateFunctionsRef.current = {
      mutatePendingOrders,
      mutateConfirmedOrders,
      mutateDeliveredOrders
    };
  };

  const setupOrderStatusChangeListener = () => {
    window.addEventListener('orderStatusChanged', handleOrderStatusChange);
    return () => window.removeEventListener('orderStatusChanged', handleOrderStatusChange);
  };

  const formattedPendingOrders = useMemo(() => formatOrderFinances(pendingOrders), [pendingOrders]);
  const formattedConfirmedOrders = useMemo(() => formatOrderFinances(confirmedOrders), [confirmedOrders]);
  const formattedDeliveredOrders = useMemo(() => formatOrderFinances(deliveredOrders), [deliveredOrders]);

  const handleLoadMorePending = useCallback(() => {
    if (isValidatingPending || isLoadingPending) return;
    if (hasMorePages(pendingOrdersPages, 10)) setPendingSize(prev => prev + 1);
  }, [isValidatingPending, isLoadingPending, pendingOrdersPages, setPendingSize]);

  const handleLoadMoreConfirmed = useCallback(() => {
    if (isValidatingConfirmed || isLoadingConfirmed) return;
    if (hasMorePages(confirmedOrdersPages, 10)) setConfirmedSize(prev => prev + 1);
  }, [isValidatingConfirmed, isLoadingConfirmed, confirmedOrdersPages, setConfirmedSize]);

  const handleLoadMoreDelivered = useCallback(() => {
    if (isValidatingDelivered || isLoadingDelivered) return;
    if (hasMorePages(deliveredOrdersPages, 10)) setDeliveredSize(prev => prev + 1);
  }, [isValidatingDelivered, isLoadingDelivered, deliveredOrdersPages, setDeliveredSize]);

  const hasNoResults =
    !isLoadingAnyOrderStatus &&
    formattedPendingOrders?.length === 0 &&
    formattedConfirmedOrders?.length === 0 &&
    formattedDeliveredOrders?.length === 0;

  const columns = [
    {
      title: 'Por confirmar',
      subtitle: 'Pendiente de pago',
      variantIcon: BORD_ONE_TONE_ICONS.clockCircle,
      items: formattedPendingOrders,
      isLoading: isLoadingPending,
      isLoadingMoreItems: isValidatingPending,
      hasMoreItems: hasMorePages(pendingOrdersPages, 10),
      onLoadMore: handleLoadMorePending,
      columnType: COLUMN_TYPE.WARNING
    },
    {
      title: 'En proceso',
      subtitle: 'Ordenes confirmadas y en proceso',
      variantIcon: BORD_ONE_TONE_ICONS.check,
      items: formattedConfirmedOrders,
      isLoading: isLoadingConfirmed,
      isLoadingMoreItems: isValidatingConfirmed,
      hasMoreItems: hasMorePages(confirmedOrdersPages, 10),
      onLoadMore: handleLoadMoreConfirmed,
      columnType: COLUMN_TYPE.SUCCESS
    },
    {
      title: 'Entregado',
      subtitle: 'Todos los envíos fueron completados',
      variantIcon: BORD_ONE_TONE_ICONS.doubleCheck,
      items: formattedDeliveredOrders,
      isLoading: isLoadingDelivered,
      isLoadingMoreItems: isValidatingDelivered,
      hasMoreItems: hasMorePages(deliveredOrdersPages, 10),
      onLoadMore: handleLoadMoreDelivered,
      columnType: COLUMN_TYPE.DELIVERED
    }
  ];

  useEffect(() => {
    updateMutateFunctionsRef();
  }, [mutatePendingOrders, mutateConfirmedOrders, mutateDeliveredOrders]);

  useEffect(() => setupOrderStatusChangeListener(), [handleOrderStatusChange]);

  useEffect(() => {
    onLoadingChange(isLoadingAnyOrderStatus);
  }, [isLoadingAnyOrderStatus, onLoadingChange]);

  return (
    <>
      {hasNoResults ? (
        <UnsuccessfulSearch
          iconSize={92}
          customText="No hay resultados para tu búsqueda."
          paddingTop={265}
          customClassName="unsuccessfulSearchOrdersListingFinances"
          customContainerIconClassName="unsuccessfulSearchOrdersListingFinancesIcon"
        />
      ) : (
        <div className="columnsContainerFinances">
          {columns?.map(column => (
            <TrelloColumn
              key={column.title}
              title={column.title}
              subtitle={column.subtitle}
              variantIcon={column.variantIcon}
              items={column?.items}
              isLoading={column?.isLoading}
              columnType={column?.columnType}
              content={column?.items?.map(item => (
                <FinanceCard
                  key={item?.orderId}
                  data={item}
                  queryParams={queryParams}
                  setQueryParams={setQueryParams}
                  isPendingConfirmation={column?.columnType === COLUMN_TYPE.WARNING}
                />
              ))}
              onLoadMore={column?.onLoadMore}
              hasMoreItems={column?.hasMoreItems}
              isLoadingMoreItems={column?.isLoadingMoreItems}
            />
          ))}
        </div>
      )}
      {openCancelOrdersModal && (
        <OrdersCancelledModal
          isOpen={openCancelOrdersModal}
          onClose={onCloseCancelOrdersModal}
          parentQueryParams={queryParams}
        />
      )}
    </>
  );
};

const OrdersListingFinances = () => {
  const location = useLocation();
  const history = useHistory();
  const { ordersSearchbarText, propertyToFilterSearch } = useFiltersState();
  const searchTypeKey = propertyToFilterSearch?.propertyToFilter;
  const { orderFilters, sortFilter, resetOrdersFinancesState } = useOrdersFinancesModule();
  const { cache } = useSWRConfig();

  const hasResetRef = useRef(false);

  if (!hasResetRef.current) {
    hasResetRef.current = true;
    resetOrdersFinancesState();
    if (cache instanceof Map) {
      const keysToDelete: (string | symbol)[] = [];
      cache.forEach((value: any, key: any) => {
        if (
          typeof key === 'string' &&
          (key.includes('orders-finances-pending') ||
            key.includes('orders-finances-confirmed') ||
            key.includes('orders-finances-delivered'))
        ) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => cache.delete(key));
    }
  }

  const [openCancelOrdersModal, setOpenCancelOrdersModal] = useState(false);
  const [countryCode, setCountryCode] = useState<string>('');
  const [organizationName, setOrganizationName] = useState<string>('');
  const [isContentLoading, setIsContentLoading] = useState(true);

  const filtersQueryParamString = useMemo(() => {
    const filteredFilters = orderFilters?.filter(filter => filter.name !== 'sort' && filter.values?.length > 0) || [];

    if (ordersSearchbarText && searchTypeKey && SEARCH_TYPE_TO_FILTER_CONFIG[searchTypeKey]) {
      const filterConfig = SEARCH_TYPE_TO_FILTER_CONFIG[searchTypeKey];
      const searchFilter: ImoduleFilter = {
        name: filterConfig.name,
        includes: filterConfig.includes,
        values: [ordersSearchbarText.trim()]
      };
      filteredFilters.push(searchFilter);
    }

    return filteredFilters?.length > 0 ? JSON.stringify(filteredFilters) : '';
  }, [orderFilters, ordersSearchbarText, searchTypeKey]);

  const filtersQueryParam = useMemo(() => {
    return filtersQueryParamString ? `filters=${filtersQueryParamString}` : '';
  }, [filtersQueryParamString]);

  const sortQueryParam = useMemo(() => {
    const sortFilterFromOrderFilters = orderFilters?.find(filter => filter.name === 'sort');
    const propertyToSort = sortFilterFromOrderFilters?.values?.[0] || 'orderId';

    return `sort=${JSON.stringify({
      value: sortFilter?.value || SORT_ORDER.DESC,
      propertyToSort: propertyToSort
    })}`;
  }, [sortFilter, orderFilters]);

  const countryQueryParam = useMemo(() => {
    return countryCode ? `country=${encodeURIComponent(countryCode)}` : '';
  }, [countryCode]);

  const organizationQueryParam = useMemo(() => {
    return organizationName ? `organization=${encodeURIComponent(organizationName)}` : '';
  }, [organizationName]);

  const debouncedFiltersQuery = useDebounce(filtersQueryParam, DEBOUNCE_DURATION);

  const queryParams = useMemo(() => {
    const hasFilters = !!debouncedFiltersQuery?.length;
    const hasCountry = !!countryQueryParam?.length;
    const hasOrganization = !!organizationQueryParam?.length;
    const hasSort = !!sortQueryParam?.length;

    const queryParts: string[] = [];
    if (hasFilters) queryParts.push(debouncedFiltersQuery);
    if (hasCountry) queryParts.push(countryQueryParam);
    if (hasOrganization) queryParts.push(organizationQueryParam);
    if (hasSort) queryParts.push(sortQueryParam);

    if (queryParts?.length === 0) return '';
    return `?${queryParts.join('&')}`;
  }, [debouncedFiltersQuery, countryQueryParam, organizationQueryParam, sortQueryParam]);

  const handleSetQueryParams = useCallback(
    (queryString: string) => {
      const normalized = queryString.startsWith('?') ? queryString.slice(1) : queryString;
      const params = new URLSearchParams(normalized);
      const countryParam = params.get('country');
      const organizationParam = params.get('organization');

      setCountryCode(countryParam ? decodeURIComponent(countryParam) : '');
      setOrganizationName(organizationParam ? decodeURIComponent(organizationParam) : '');

      history.replace({ pathname: location.pathname, search: normalized ? `?${normalized}` : '' });
    },
    [history, location.pathname]
  );

  const handleOpenOrCloseCancelOrdersModal = useCallback(() => {
    setOpenCancelOrdersModal(prev => !prev);
  }, []);

  const handleLoadingChange = useCallback((isLoading: boolean) => {
    setIsContentLoading(isLoading);
  }, []);

  const swrKeyCancelledPrefetch = useMemo(
    () => (queryParams ? ['orders-cancelled-modal', ORDER_STATUS_ID_CANCELLED, queryParams] : null),
    [queryParams]
  );
  const getCancelledOrdersPrefetchKey = useGetSWRInfiniteKey(swrKeyCancelledPrefetch);
  const { data: cancelledOrdersPagesPrefetch = [] } = useSWRInfinite(
    getCancelledOrdersPrefetchKey,
    (key: (string | number)[]) => {
      const pageIndex = key?.[key?.length - 1] as number;
      const processedParams = processQueryParams(queryParams);
      return getShipmentOrderFinancesCancelled({
        page: pageIndex + 1,
        size: 10,
        additionalQueryParams: processedParams
      });
    },
    {
      ...optionsParamsSwr({ errorRetryCount: 0, suspense: false }),
      revalidateFirstPage: false,
      revalidateOnFocus: false
    }
  );
  const hasCancelledOrdersMatchingSearch = !!ordersSearchbarText && cancelledOrdersPagesPrefetch.flat().length > 0;
  const showCanceledOrders = hasCancelledOrdersMatchingSearch;

  return (
    <div id="ordersListingFinances">
      <div className="containerHeaderFinances">
        <OrdersTrackingFinanceFilters />
        <ButtonCancelOrders
          handleClick={handleOpenOrCloseCancelOrdersModal}
          buttonText="Ver canceladas"
          isDisabled={isContentLoading}
          showCanceledOrders={showCanceledOrders}
        />
      </div>
      <OrdersListingFinancesContent
        key={queryParams}
        queryParams={queryParams}
        setQueryParams={handleSetQueryParams}
        openCancelOrdersModal={openCancelOrdersModal}
        onCloseCancelOrdersModal={handleOpenOrCloseCancelOrdersModal}
        onLoadingChange={handleLoadingChange}
      />
    </div>
  );
};

export default OrdersListingFinances;
